/**
 * MusicTracks - Centralized music track definitions and loading
 * Manages all background music for the game
 */

import { audioManager } from '../../engine/AudioManager';

/**
 * Music track identifiers
 */
export enum MusicTrack {
  MENU = 'menu',
  PIANO_AMBIENT = 'piano-ambient',
  GAMEPLAY = 'gameplay',
  NIGHT_1 = 'night-1',
  NIGHT_2 = 'night-2',
  NIGHT_3 = 'night-3',
  NIGHT_4 = 'night-4',
  NIGHT_5 = 'night-5',
  NIGHT_6 = 'night-6',
  NIGHT_7 = 'night-7',
  JUMPSCARE = 'jumpscare',
  VICTORY = 'victory',
  GAME_OVER = 'game-over',
}

/**
 * Music track configuration
 */
const MUSIC_CONFIGS = {
  [MusicTrack.PIANO_AMBIENT]: {
    src: ['/sounds/Jose F. Press - Piano Loops.mp3'],
    volume: 0.6,
    loop: true,
  },
  [MusicTrack.GAMEPLAY]: {
    src: ['/sounds/488108__emrebugday__mysterious-horror-theme-song.mp3'],
    volume: 0.5,
    loop: true,
  },
  // Add more tracks as they become available
  // [MusicTrack.MENU]: {
  //   src: ['assets/audio/music/menu-theme.mp3'],
  //   volume: 0.7,
  //   loop: true,
  // },
};

/**
 * Load all music tracks into the audio manager
 */
export function loadAllMusicTracks(): void {
  console.log('MusicTracks: Loading all music tracks...');

  // Load piano ambient music (cutscenes, menus, night complete)
  if (MUSIC_CONFIGS[MusicTrack.PIANO_AMBIENT]) {
    audioManager.loadMusic(MusicTrack.PIANO_AMBIENT, MUSIC_CONFIGS[MusicTrack.PIANO_AMBIENT]);
  }

  // Load gameplay music (during active gameplay)
  if (MUSIC_CONFIGS[MusicTrack.GAMEPLAY]) {
    audioManager.loadMusic(MusicTrack.GAMEPLAY, MUSIC_CONFIGS[MusicTrack.GAMEPLAY]);
  }

  // Add more tracks here as they're created
  // Example:
  // if (MUSIC_CONFIGS[MusicTrack.MENU]) {
  //   audioManager.loadMusic(MusicTrack.MENU, MUSIC_CONFIGS[MusicTrack.MENU]);
  // }

  console.log('MusicTracks: All available music tracks loaded');
}

/**
 * Play a specific music track with optional fade-in
 */
export function playMusicTrack(track: MusicTrack, fadeIn: boolean = true): void {
  audioManager.playMusic(track, fadeIn);
  console.log(`MusicTracks: Playing "${track}"`);
}

/**
 * Stop the current music with optional fade-out
 */
export function stopMusic(fadeOut: boolean = true): void {
  audioManager.stopMusic(fadeOut);
}

/**
 * Pause the current music
 */
export function pauseMusic(): void {
  audioManager.pauseMusic();
}

/**
 * Resume the paused music
 */
export function resumeMusic(): void {
  audioManager.resumeMusic();
}

/**
 * Get the currently playing track
 */
export function getCurrentTrack(): string | null {
  return audioManager.getCurrentMusicTrack();
}

/**
 * Switch between music tracks with crossfade
 */
export function switchMusicTrack(newTrack: MusicTrack, crossfade: boolean = true): void {
  if (crossfade) {
    audioManager.playMusic(newTrack, true); // This automatically fades out the current track
  } else {
    audioManager.stopMusic(false);
    audioManager.playMusic(newTrack, false);
  }
  console.log(`MusicTracks: Switched to "${newTrack}"`);
}
